import { SESSION_TIMEOUT } from '@/services/constant';
import { IndexedDbService } from '@/services/indexedDb';
import { createStore } from 'vuex';
import airmapStore from './modules/airmapStore.js';
import downloadStore from './modules/downloadStore.js';
import homeStore from './modules/homeStore.js';
import userStore from './modules/userStore.js';

const STORAGE_KEY = 'vuex';

const idbPlugin = store => {
  let timer = null;
  let stopSaving = false;

  store.subscribe((mutation, state) => {
    if (mutation.type === 'setLoggedOut') {
      stopSaving = true;
      if (timer) clearTimeout(timer);
      return;
    }
    if (mutation.type === 'setLoggedIn') {
      stopSaving = false;
    }
    if (stopSaving || mutation.type === 'toggleSpinner') return;
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      const stateToPersist = {
        homeStore: state.homeStore,
        userStore: state.userStore,
        loginTime: state.loginTime,
      };
      IndexedDbService.setItem(STORAGE_KEY, stateToPersist);
    }, 2500);
  });
};

const getInitialState = () => ({
  isSpinnerShowing: false,
  isLoggedOut: false,
  loginTime: Date.now(),
});

const store = createStore({
  state: getInitialState(),
  actions: {
    toggleSpinner({ commit }) {
      commit('toggleSpinner');
    },
    async logout({ commit, dispatch }) {
      commit('setLoggedOut');
      dispatch('homeStore/resetModule', null, { root: true });
      dispatch('userStore/resetModule', null, { root: true });
      dispatch('airmapStore/resetModule', null, { root: true });
      dispatch('downloadStore/resetModule', null, { root: true });

      try {
        localStorage.clear();
        sessionStorage.clear();
        await IndexedDbService.clear();
      } catch (error) {
        // Silently fail
      }
      commit('resetRoot');
    },
    async restoreState({ commit }) {
      try {
        const storedState = await IndexedDbService.getItem(STORAGE_KEY);
        if (storedState) {
          const sessionAge = Date.now() - (storedState.loginTime || 0);
          if (sessionAge > SESSION_TIMEOUT) {
            await IndexedDbService.clear();
            localStorage.clear();
            sessionStorage.clear();
            return null;
          }
          commit('setLoggedIn');
          return Object.assign({}, storedState);
        }
        return null;
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Failed to restore state:', err);
        }
        return null;
      }
    },
  },
  mutations: {
    setLoggedOut(state) {
      state.isLoggedOut = true;
    },
    setLoggedIn(state) {
      state.isLoggedOut = false;
    },
    toggleSpinner(state) {
      state.isSpinnerShowing = !state.isSpinnerShowing;
    },
    resetRoot(state) {
      Object.assign(state, getInitialState());
      state.isLoggedOut = true;
    },
  },
  modules: { homeStore, userStore, airmapStore, downloadStore },
  plugins: [idbPlugin],
});

export default store;
